import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ref, onValue, off } from "firebase/database";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import { realtimeDb } from "../helper/firebase";

const ADMIN_ID = "1111";
const ADMIN_JOB_CHAT_PATH = "adminMessageChat";

const isMessageDeletedForAdmin = (message) => {
  return (
    message?.deletedFor?.[ADMIN_ID] === true ||
    message?.deletedFor?.[String(ADMIN_ID)] === true
  );
};

const isMessageDeletedForEveryone = (message) => {
  return message?.deletedForEveryone === true;
};

const getMessageSenderId = (message) => {
  return String(
    message?.user?._id ||
      message?.sender_id ||
      message?.senderId ||
      ""
  );
};

const isUnreadForAdmin = (message) => {
  const senderId = getMessageSenderId(message);
  const readByAdmin = Boolean(message?.readBy?.[String(ADMIN_ID)]);

  const isFromElectrician =
    senderId && senderId !== String(ADMIN_ID);

  return (
    isFromElectrician &&
    !readByAdmin &&
    message?.status !== "deleted" &&
    !isMessageDeletedForAdmin(message) &&
    !isMessageDeletedForEveryone(message)
  );
};

const MasterLayout = ({ children }) => {
  const [sidebarActive, seSidebarActive] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [driverChatUnreadCount, setDriverChatUnreadCount] = useState(0);

  const location = useLocation();

  useEffect(() => {
    const handleDropdownClick = (event) => {
      event.preventDefault();

      const clickedLink = event.currentTarget;
      const clickedDropdown = clickedLink.closest(".dropdown");

      if (!clickedDropdown) return;

      const isActive = clickedDropdown.classList.contains("open");

      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");

      allDropdowns.forEach((dropdown) => {
        dropdown.classList.remove("open");
      });

      if (!isActive) {
        clickedDropdown.classList.add("open");
      }
    };

    const dropdownTriggers = document.querySelectorAll(
      ".sidebar-menu .dropdown > a, .sidebar-menu .dropdown > Link"
    );

    dropdownTriggers.forEach((trigger) => {
      trigger.addEventListener("click", handleDropdownClick);
    });

    const openActiveDropdown = () => {
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");

      allDropdowns.forEach((dropdown) => {
        const submenuLinks = dropdown.querySelectorAll(".sidebar-submenu li a");

        submenuLinks.forEach((link) => {
          if (
            link.getAttribute("href") === location.pathname ||
            link.getAttribute("to") === location.pathname
          ) {
            dropdown.classList.add("open");
          }
        });
      });
    };

    openActiveDropdown();

    return () => {
      dropdownTriggers.forEach((trigger) => {
        trigger.removeEventListener("click", handleDropdownClick);
      });
    };
  }, [location.pathname]);

  useEffect(() => {
    const adminJobChatRef = ref(realtimeDb, ADMIN_JOB_CHAT_PATH);

    const unsubscribe = onValue(adminJobChatRef, (snapshot) => {
      if (!snapshot.exists()) {
        setDriverChatUnreadCount(0);
        return;
      }

      const allJobChats = snapshot.val();
      let totalUnread = 0;

      Object.entries(allJobChats).forEach(([jobId, jobMessages]) => {
        if (!jobMessages || typeof jobMessages !== "object") return;

        Object.values(jobMessages).forEach((message) => {
          if (isUnreadForAdmin(message)) {
            totalUnread += 1;
          }
        });
      });

      setDriverChatUnreadCount(totalUnread);
    });

    return () => {
      off(adminJobChatRef, "value", unsubscribe);
    };
  }, []);

  const sidebarControl = () => {
    seSidebarActive(!sidebarActive);
  };

  const mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  return (
    <section className={mobileMenu ? "overlay active" : "overlay "}>
      <aside
        className={
          sidebarActive
            ? "sidebar active "
            : mobileMenu
            ? "sidebar sidebar-open"
            : "sidebar"
        }
      >
        <button
          onClick={mobileMenuControl}
          type="button"
          className="sidebar-close-btn"
        >
          <Icon icon="radix-icons:cross-2" />
        </button>

        <div>
          <Link to="/" className="sidebar-logo">
            <img
              src="assets/images/logo.png"
              alt="site logo"
              className="light-logo"
            />
            <img
              src="assets/images/logo-light.png"
              alt="site logo"
              className="dark-logo"
            />
            <img
              src="assets/images/logo-icon.png"
              alt="site logo"
              className="logo-icon"
            />
          </Link>
        </div>

        <div className="sidebar-menu-area">
          <ul className="sidebar-menu" id="sidebar-menu">
            <li>
              <NavLink
                to="/"
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon
                  icon="solar:home-smile-angle-outline"
                  className="menu-icon"
                />
                <span>Dashboard</span>
              </NavLink>
            </li>

            <li className="dropdown">
              <Link to="#">
                <Icon icon="hugeicons:briefcase-01" className="menu-icon" />
                <span>Jobs</span>
              </Link>

              <ul className="sidebar-submenu">
                <li>
                  <NavLink
                    to="/add-job"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className="ri-circle-fill circle-icon text-info-main w-auto" />{" "}
                    Add new
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/jobs"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className="ri-circle-fill circle-icon text-primary-600 w-auto" />{" "}
                    View All Jobs
                  </NavLink>
                </li>
              </ul>
            </li>

            <li>
              <NavLink
                to="/customers"
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon
                  icon="flowbite:users-group-outline"
                  className="menu-icon"
                />
                <span>Customers</span>
              </NavLink>
            </li>

            <li className="dropdown">
              <Link to="#">
                <Icon icon="hugeicons:invoice-03" className="menu-icon" />
                <span>Invoices & Quotes</span>
              </Link>

              <ul className="sidebar-submenu">
                <li>
                  <NavLink
                    to="/create-invoice"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className="ri-circle-fill circle-icon text-info-main w-auto" />{" "}
                    Create Invoice
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/create-quote"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className="ri-circle-fill circle-icon text-info-main w-auto" />{" "}
                    Create Quote
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/invoices"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className="ri-circle-fill circle-icon text-primary-600 w-auto" />{" "}
                    Invoices
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/quotes"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className="ri-circle-fill circle-icon text-primary-600 w-auto" />{" "}
                    Quotes
                  </NavLink>
                </li>
              </ul>
            </li>

            <li>
              <NavLink
                to="/staff"
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon icon="mdi:users" className="menu-icon" />
                <span>Staff</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/timesheet"
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon icon="mdi:calendar" className="menu-icon" />
                <span>Timesheet</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/electrician-chat"
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon
                  icon="solar:chat-round-dots-linear"
                  className="menu-icon"
                />

                <span className="d-flex align-items-center justify-content-between w-100">
                  <span>Electrician Chats</span>

                  {driverChatUnreadCount > 0 ? (
                    <span
                      className="ms-2 d-inline-flex align-items-center justify-content-center rounded-pill bg-danger text-white fw-semibold"
                      style={{
                        minWidth: "22px",
                        height: "22px",
                        fontSize: "11px",
                        padding: "0 7px",
                        lineHeight: "22px",
                        boxShadow: "0 4px 10px rgba(220,53,69,0.35)",
                      }}
                    >
                      {driverChatUnreadCount > 99
                        ? "99+"
                        : driverChatUnreadCount}
                    </span>
                  ) : null}
                </span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/overtime"
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon icon="mdi:clock" className="menu-icon" />
                <span>Overtime Requests</span>
              </NavLink>
            </li>

            <li className="dropdown">
              <Link to="#">
                <Icon icon="hugeicons:chart-01" className="menu-icon" />
                <span>Stock</span>
              </Link>

              <ul className="sidebar-submenu">
                <li>
                  <NavLink
                    to="/add-stock"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className="ri-circle-fill circle-icon text-info-main w-auto" />{" "}
                    Add new
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/stock"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className="ri-circle-fill circle-icon text-primary-600 w-auto" />{" "}
                    List
                  </NavLink>
                </li>
              </ul>
            </li>

            <li className="dropdown">
              <Link to="#">
                <Icon
                  icon="hugeicons:customer-service-01"
                  className="menu-icon"
                />
                <span>Services</span>
              </Link>

              <ul className="sidebar-submenu">
                <li>
                  <NavLink
                    to="/add-service"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className="ri-circle-fill circle-icon text-info-main w-auto" />{" "}
                    Add new
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/services"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className="ri-circle-fill circle-icon text-primary-600 w-auto" />{" "}
                    List
                  </NavLink>
                </li>
              </ul>
            </li>

            <li>
              <NavLink
                to="/company"
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon icon="hugeicons:building-02" className="menu-icon" />
                <span>Company</span>
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/policies"
                className={(navData) => (navData.isActive ? "active-page" : "")}
              >
                <Icon icon="hugeicons:invoice-01" className="menu-icon" />
                <span>Policies</span>
              </NavLink>
            </li>
          </ul>
        </div>
      </aside>

      <main className={sidebarActive ? "dashboard-main active" : "dashboard-main"}>
        <div className="navbar-header">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-4">
                <button
                  type="button"
                  className="sidebar-toggle"
                  onClick={sidebarControl}
                >
                  {sidebarActive ? (
                    <Icon
                      icon="iconoir:arrow-right"
                      className="icon text-2xl non-active"
                    />
                  ) : (
                    <Icon
                      icon="heroicons:bars-3-solid"
                      className="icon text-2xl non-active "
                    />
                  )}
                </button>

                <button
                  onClick={mobileMenuControl}
                  type="button"
                  className="sidebar-mobile-toggle"
                >
                  <Icon icon="heroicons:bars-3-solid" className="icon" />
                </button>
              </div>
            </div>

            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-3">
                {/* <ThemeToggleButton /> */}

                <div className="dropdown">
                  <button
                    className="d-flex justify-content-center align-items-center rounded-circle"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <img
                      src="assets/images/user.png"
                      alt="image_user"
                      className="w-40-px h-40-px object-fit-cover rounded-circle"
                    />
                  </button>

                  <div className="dropdown-menu to-top dropdown-menu-sm">
                    <div className="py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2">
                      <div>
                        <h6 className="text-lg text-primary-light fw-semibold mb-2">
                          Admin
                        </h6>
                        <span className="text-secondary-light fw-medium text-sm">
                          Admin
                        </span>
                      </div>

                      <button type="button" className="hover-text-danger">
                        <Icon
                          icon="radix-icons:cross-1"
                          className="icon text-xl"
                        />
                      </button>
                    </div>

                    <ul className="to-top-list">
                      <li>
                        <Link
                          className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3"
                          onClick={(e) => {
                            e.preventDefault();
                            localStorage.removeItem("user");
                            window.location.href = "/sign-in";
                          }}
                        >
                          <Icon icon="lucide:power" className="icon text-xl" />
                          Log Out
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-main-body">{children}</div>

        <footer className="d-footer">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <p className="mb-0">© 2024 RWIRE. All Rights Reserved.</p>
            </div>

            <div className="col-auto">
              <p className="mb-0">
                Made by <span className="text-primary-600">RWIRE</span>
              </p>
            </div>
          </div>
        </footer>
      </main>
    </section>
  );
};

export default MasterLayout;